
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { LogIn, UserPlus, Sparkles } from "lucide-react";

/**
 * MobileMenuAuth Component
 * 
 * Authentication CTAs for non-authenticated users.
 * Phase 3.3: Enhanced with benefits banner and improved button hierarchy
 * 
 * Features:
 * - Benefits banner highlighting premium features
 * - Animated sparkles icon
 * - Primary "Create Account" button with gradient
 * - Secondary "Sign In" button with outline style
 * - Spring animations for playful feel
 * - Clear visual hierarchy between actions
 * 
 * @param {MobileMenuAuthProps} props - Component props
 * @returns {JSX.Element} Authentication section
 */

interface MobileMenuAuthProps {
  handleLinkClick: (path: string) => void;
}

const MobileMenuAuth = ({ handleLinkClick }: MobileMenuAuthProps) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      transition={{ delay: 0.2, type: "spring", stiffness: 200 }} 
      className="space-y-4"
    >
      {/* Benefits Banner */}
      <motion.div
        className="p-3 rounded-2xl bg-gradient-to-br from-primary/15 dark:from-primary/10 via-secondary/10 dark:via-secondary/8 to-accent/10 border border-primary/30 dark:border-primary/20"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <div className="flex items-start gap-2.5">
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          >
            <Sparkles className="h-4 w-4 text-primary mt-0.5" />
          </motion.div>
          <div>
            <p className="text-xs font-semibold text-foreground mb-1">
              Unlock Premium Features
            </p>
            <ul className="text-[10px] text-muted-foreground/90 dark:text-muted-foreground/80 space-y-0.5">
              <li>• Exclusive deals & early access</li>
              <li>• Track orders & manage wishlist</li>
              <li>• Personalized recommendations</li>
            </ul>
          </div>
        </div>
      </motion.div>

      {/* Sign Up Button (Primary) */}
      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
        <Button 
          className="w-full h-12 rounded-2xl bg-gradient-to-r from-primary via-primary/90 to-secondary hover:from-primary/90 hover:via-primary/80 hover:to-secondary/90 transition-all duration-300 group shadow-lg hover:shadow-xl text-sm font-bold" 
          onClick={() => handleLinkClick('/auth')}
        >
          <motion.div
            whileHover={{ rotate: 360 }}
            transition={{ duration: 0.5 }}
          >
            <UserPlus className="mr-2 h-4 w-4" />
          </motion.div>
          <span>Create Account</span>
        </Button>
      </motion.div>

      {/* Sign In Button (Secondary) */}
      <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}>
        <Button 
          variant="outline" 
          className="w-full h-10 rounded-2xl border-2 border-primary/30 hover:border-primary/50 hover:bg-primary/5 transition-all duration-300 group text-sm font-semibold" 
          onClick={() => handleLinkClick('/auth')}
        >
          <LogIn className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform duration-200" />
          <span>Sign In</span>
        </Button>
      </motion.div>
    </motion.div>
  );
};

export default MobileMenuAuth;
